import React from 'react';
import { getArenaLeaderboard } from '../services/arenaService';

function fmt(ms) {
  if (!ms && ms !== 0) return '—';
  return `${ms}ms`;
}

function LeaderboardRow({ row }) {
  const medalColors = ['', 'text-amber-500', 'text-slate-400', 'text-orange-400'];
  const medalColor = row.rank <= 3 ? medalColors[row.rank] : '';
  const bg = row.isUser
    ? 'bg-purple-50 border-purple-300'
    : 'bg-white border-slate-200 hover:border-purple-200 hover:shadow-sm';

  return (
    <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${bg}`}>
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-slate-100 ${medalColor || 'text-slate-500'}`}>
        {row.rank <= 3
          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z"/></svg>
          : row.rank}
      </span>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-orange-500 text-xs font-bold text-white shadow-sm">
        {(row.name || 'AN').slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {row.name} {row.isUser && <span className="text-xs text-purple-500">(You)</span>}
        </div>
        <div className="truncate text-xs text-slate-400">{row.college}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-bold text-slate-900">{row.score ?? 0} pts</div>
        <div className="text-xs text-orange-500">{fmt(row.averageReactionTime)}</div>
      </div>
    </div>
  );
}

export default function ReflexLeaderboard({ onBack, currentUserUid }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    getArenaLeaderboard('reflex-tap')
      .then(data => setRows((data.leaderboard || []).map(r => ({ ...r, isUser: r.uid === currentUserUid }))))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [currentUserUid]);

  const top3 = rows.slice(0, 3);

  return (
    <div
      className="flex flex-col"
      style={{
        minHeight: '100dvh',
        background: 'radial-gradient(circle at 10% 0%, rgba(124,58,237,0.08), transparent 35%), radial-gradient(circle at 90% 5%, rgba(249,115,22,0.07), transparent 30%), #f8fafc',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-3">
          <button
            onClick={onBack}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition shadow-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div>
            <div className="text-sm font-extrabold text-slate-900">Leaderboard</div>
            <div className="text-xs text-purple-500">Today · Reflex Tap</div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-lg px-4 py-5">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-500 border-t-transparent" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-400">No scores yet today. Be the first!</div>
        ) : (
          <>
            {top3.length === 3 && (
              <div className="mb-5 flex items-end justify-center gap-4">
                {[top3[1], top3[0], top3[2]].map((row, i) => {
                  const heights = ['h-20', 'h-28', 'h-16'];
                  const podiumBg = [
                    'bg-slate-100 border-slate-200',
                    'bg-gradient-to-b from-purple-100 to-orange-50 border-purple-200',
                    'bg-orange-50 border-orange-200',
                  ];
                  return (
                    <div key={row.rank} className="flex flex-col items-center gap-2">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-orange-500 text-sm font-bold text-white shadow-md shadow-purple-200">
                        {(row.name || 'A').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="text-center">
                        <div className="text-[11px] font-bold text-slate-900 max-w-[72px] truncate">{(row.name || 'Anonymous').split(' ')[0]}</div>
                        <div className="text-[10px] text-orange-500 font-semibold">{row.score ?? 0} pts</div>
                      </div>
                      <div className={`flex w-20 ${heights[i]} items-start justify-center rounded-t-xl pt-2 border ${podiumBg[i]}`} />
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-2">
              {rows.map((row, i) => (
                <LeaderboardRow key={i} row={{ ...row, rank: i + 1 }} />
              ))}
            </div>
          </>
        )}

        {!currentUserUid && (
          <div className="mt-5 rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-orange-50 p-4 text-center">
            <p className="mb-3 text-sm text-slate-600">Sign in to appear on the leaderboard</p>
            <a href="/signup" className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-purple-200">
              Join Now →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
